import { metrics, registry } from '@geo-admin/shared/contract/api/index.ts';
import { EngineTabs, engineLabel, engineSearch, useEngine } from '../components/EngineTabs.tsx';
import type { Brand, DeletedBrand } from '@geo-admin/shared/contract/api/registry.ts';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Dialog } from '../components/ui/dialog.tsx';
import { FieldError, Input, Label } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';
import { PartnerCard } from '../components/PartnerCard.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandArticlePhotosAdmin } from '../components/BrandArticlePhotosAdmin.tsx';
import { Tabs, TestBadge } from '../components/BrandKindTabs.tsx';
import type { BrandKind } from '@geo-admin/shared/contract/common.ts';
import { AddQueryAdmin, BrandAccountsAdmin, BrandInfoAdmin } from '../components/BrandInfoAdmin.tsx';
import { BrandReportCardAdmin } from '../components/BrandReportCardAdmin.tsx';
import { BrandWorksAdmin } from '../components/BrandWorksAdmin.tsx';
import { BrandBillingAdmin } from './billing.tsx';
import { Redirect } from '../components/Redirect.tsx';
import { useMe } from '../lib/me.ts';
import { PATHS, pathsFor } from '../lib/paths.ts';
import { queryColumns } from './overview.tsx';

export function BrandsPage() {
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  // 「실제 브랜드 | 테스트 | 삭제된 브랜드」 탭 — 운영자만 의미가 있다(서버가 고객 범위 호출에서는 무시한다, 261023-test-brand).
  // 주소의 `?kind=` 가 정한다(test·deleted). 삭제된 브랜드는 실제·테스트를 함께 보이고 테스트에 배지를 단다
  const tab = useBrandsTab();
  const navigate = useNavigate();
  const kind: BrandKind = tab === 'test' ? 'test' : 'real';
  const showDeleted = tab === 'deleted';
  const brands = useApi(registry.listBrands, { query: { brandKind: kind } }, { enabled: !showDeleted });
  const [creating, setCreating] = useState(false);
  const del = useApiMutation(registry.deleteBrand);
  const p = pathsFor(me.data?.role);

  const columns: ColumnDef<Brand, unknown>[] = [
    {
      header: '브랜드',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <Link to={p.brand} params={{ id: row.original.id }} className="font-medium hover:underline">
            {row.original.name}
          </Link>
          {/* 소유 계정의 회사명(가입의 업체명) — 운영자 응답에만 실린다 */}
          {row.original.ownerCompanyNames.length > 0 ? (
            <div className="text-slate-500 text-xs">{row.original.ownerCompanyNames.join(' · ')}</div>
          ) : null}
        </div>
      ),
    },
    { header: '질문 수', accessorKey: 'queryCount' },
  ];
  if (isAdmin) {
    columns.splice(1, 0, {
      header: '목표 브랜드명',
      id: 'brandNames',
      cell: ({ row }) =>
        row.original.brandNames.length > 0 ? (
          row.original.brandNames.join(' · ')
        ) : (
          <span className="text-slate-400">없음</span>
        ),
    });
    // 판매 대행사 — 운영자 응답에만 실린다(261025-agency-white-label). 없으면 liveGPT 직접 고객
    columns.splice(2, 0, {
      header: '대행사',
      id: 'agency',
      cell: ({ row }) =>
        row.original.agencyName ? (
          <Badge tone="blue">{row.original.agencyName}</Badge>
        ) : (
          <span className="text-slate-400">직접</span>
        ),
    });
    // 소속 계정 — 0 이면 아무도 관리하지 않는 브랜드다(261013-account-brand-membership)
    columns.push({
      header: '계정',
      id: 'accounts',
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className="flex flex-wrap items-center gap-1">
            <span className="tabular-nums">{c.accountCount}</span>
            {c.status === 'pending' ? <Badge tone="amber">승인 대기</Badge> : null}
            {c.status === 'confirmed' ? <Badge tone="blue">결제 대기</Badge> : null}
            {c.accountCount === 0 ? <Badge tone="red">주인 없음</Badge> : null}
          </div>
        );
      },
    });
    columns.push({
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {/* 편집은 상세 페이지에서 — 보이는 자리가 곧 고치는 자리다 (운영자 결정 2026-10-11) */}
          <Link to={PATHS.admin.brand} params={{ id: row.original.id }}>
            <Button size="sm" variant="outline">
              상세
            </Button>
          </Link>
          <Button
            size="sm"
            variant="ghost"
            disabled={del.isPending}
            onClick={() => {
              if (confirm(deleteConfirm(row.original.name))) del.mutate({ params: { id: row.original.id } });
            }}
          >
            삭제
          </Button>
        </div>
      ),
    });
  }

  // 고객 계정은 자기 고객 하나뿐이다 — 목록 대신 상세로
  const brandId = me.data?.brandId;
  if (me.data?.role === 'member' && brandId)
    return <Redirect go={(n) => n({ to: p.brand, params: { id: brandId }, replace: true })} />;

  return (
    <>
      <PageTitle
        title="브랜드"
        actions={
          isAdmin ? (
            showDeleted ? null : (
              <div className="flex gap-2">
                <Button onClick={() => setCreating(true)}>
                  {kind === 'test' ? '테스트 브랜드 추가' : '브랜드 추가'}
                </Button>
              </div>
            )
          ) : null
        }
      />
      {isAdmin ? (
        <Tabs
          className="mb-4"
          tabs={BRANDS_TABS}
          value={tab}
          onChange={(t) => void navigate({ to: '.', search: (t === 'real' ? {} : { kind: t }) as never })}
        />
      ) : null}
      {del.error ? <ErrorBox error={del.error} /> : null}
      {isAdmin && showDeleted ? (
        <DeletedBrandsAdmin />
      ) : (
        <Card>
          {brands.isLoading ? (
            <Loading />
          ) : brands.error ? (
            <ErrorBox error={brands.error} />
          ) : (
            <DataTable
              columns={columns}
              data={brands.data?.brands ?? []}
              empty={kind === 'test' ? '테스트 브랜드가 없습니다' : '볼 수 있는 브랜드가 없습니다'}
            />
          )}
        </Card>
      )}
      {isAdmin ? (
        <CreateBrandDialog open={creating} isTest={kind === 'test'} onClose={() => setCreating(false)} />
      ) : null}
    </>
  );
}

const BRANDS_TABS = [
  { id: 'real', label: '실제 브랜드' },
  { id: 'test', label: '테스트' },
  { id: 'deleted', label: '삭제된 브랜드' },
] as const;
type BrandsTab = (typeof BRANDS_TABS)[number]['id'];

function useBrandsTab(): BrandsTab {
  const { kind } = useSearch({ strict: false }) as { kind?: string };
  return kind === 'test' || kind === 'deleted' ? kind : 'real';
}

/** 삭제 = 계약 종료(261022-brand-delete-is-contract-end) — 무엇이 일어나는지 확인 창에 다 적는다 */
const deleteConfirm = (name: string) =>
  `${name} 을(를) 삭제합니다(계약 종료).\n\n` +
  '· 살아 있는 구독을 지금 즉시 해지합니다(환불은 하지 않습니다)\n' +
  '· 질문을 삭제해 수집을 멈춥니다(과거 샘플은 남습니다)\n' +
  '· 소속 계정은 이 브랜드를 더 볼 수 없습니다\n' +
  '· 기사·숏폼·블로그 기록은 지우지 않습니다\n\n' +
  '「삭제된 브랜드」에서 복구할 수 있습니다(질문은 함께 돌아오고 구독은 새로 제안합니다). ' +
  '사이트 철거·랭킹 정리는 철거 런북(docs/RUNBOOK-offboarding.md)으로 따로 합니다. 계속할까요?';

/**
 * 삭제된 브랜드 — 계약 종료한 고객도 여기 있다. 복구하면 함께 지운 질문이 돌아온다(구독은 새로 제안한다)
 */
/** 실제·테스트를 함께 — 지운 때 순(최근이 먼저). 테스트였던 브랜드에는 배지를 단다 */
function DeletedBrandsAdmin() {
  const real = useApi(registry.listDeletedBrands, { query: { brandKind: 'real' } });
  const test = useApi(registry.listDeletedBrands, { query: { brandKind: 'test' } });
  const testIds = new Set((test.data?.brands ?? []).map((b) => b.id));
  const rows = [...(real.data?.brands ?? []), ...(test.data?.brands ?? [])].sort((a, b) =>
    b.deletedAt.localeCompare(a.deletedAt),
  );
  const list = { isLoading: real.isLoading || test.isLoading, error: real.error ?? test.error };
  const restore = useApiMutation(registry.restoreBrand);
  const columns: ColumnDef<DeletedBrand, unknown>[] = [
    {
      header: '브랜드',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">
            {row.original.name}
            {testIds.has(row.original.id) ? <TestBadge /> : null}
          </div>
          {row.original.homepageUrl ? (
            <div className="break-all text-slate-500 text-xs">{row.original.homepageUrl}</div>
          ) : null}
        </div>
      ),
    },
    { header: '삭제한 때', id: 'deletedAt', cell: ({ row }) => dateTime(row.original.deletedAt) },
    { header: '함께 지운 질문', accessorKey: 'queryCount' },
    { header: '소속 계정', accessorKey: 'accountCount' },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            disabled={restore.isPending}
            onClick={() => {
              if (
                confirm(
                  `${row.original.name} 을(를) 복구합니다. 함께 지운 질문 ${row.original.queryCount}개가 다시 수집되고 ` +
                    `소속 계정이 다시 들어옵니다. 해지된 구독은 돌아오지 않습니다 — 구독은 새로 제안해 주세요. 계속할까요?`,
                )
              )
                restore.mutate({ params: { id: row.original.id } });
            }}
          >
            복구
          </Button>
        </div>
      ),
    },
  ];
  return (
    <Card>
      <CardHeader title="삭제된 브랜드" description="계약 종료로 지운 브랜드를 포함한다. 최근에 지운 것이 먼저다" />
      {restore.error ? <ErrorBox error={restore.error} /> : null}
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : (
        <DataTable columns={columns} data={rows} empty="삭제된 브랜드가 없습니다" />
      )}
    </Card>
  );
}

/** 테스트 브랜드 표시 — 양방향(261023-test-brand). 바꾸면 그 브랜드가 반대쪽 탭으로 옮겨 간다 */
function BrandTestAdmin({ brandId, isTest }: { brandId: string; isTest: boolean }) {
  const set = useApiMutation(registry.setBrandTest);
  const message = isTest
    ? '실제 브랜드로 돌립니다. 「실제 브랜드」 탭에 보이고, 수집·용량·운영 알림이 실제 고객과 같이 셉니다. 계속할까요?'
    : '테스트 브랜드로 표시합니다. 「테스트」 탭으로 옮겨 가고, 수집이 실제 고객 질문 뒤로 밀리며 용량·결손·결제 안내 알림에서 빠집니다. 계속할까요?';
  return (
    <Card>
      <CardHeader
        title={isTest ? '테스트 브랜드' : '테스트 브랜드로 표시'}
        description={
          isTest
            ? '시험용 브랜드입니다 — 실제 고객 목록·합산에 섞이지 않고, 수집은 실제 고객 질문 뒤에 합니다'
            : '시험용으로 만든 브랜드라면 표시해 실제 고객과 나눕니다'
        }
        actions={
          <Button
            size="sm"
            variant="outline"
            disabled={set.isPending}
            onClick={() => {
              if (confirm(message)) set.mutate({ params: { id: brandId }, body: { isTest: !isTest } });
            }}
          >
            {isTest ? '실제 브랜드로' : '테스트로 표시'}
          </Button>
        }
      />
      {set.error ? <ErrorBox error={set.error} /> : null}
    </Card>
  );
}

/** 브랜드 상세(운영자) 맨 아래 — 삭제(계약 종료). 지우면 목록으로 돌아간다 */
function BrandDeleteAdmin({ brandId, name, isTest }: { brandId: string; name: string; isTest: boolean }) {
  const del = useApiMutation(registry.deleteBrand);
  const navigate = useNavigate();
  return (
    <Card>
      <CardHeader
        title="계약 종료(삭제)"
        description="구독 즉시 해지 · 질문 삭제 · 소속 계정의 접근 종료를 한 번에 한다. 「삭제된 브랜드」에서 복구할 수 있다"
        actions={
          <Button
            size="sm"
            variant="danger"
            disabled={del.isPending}
            onClick={async () => {
              if (!confirm(deleteConfirm(name))) return;
              const r = await del.mutateAsync({ params: { id: brandId } }).catch(() => null);
              if (r) await navigate({ to: PATHS.admin.brands, search: (isTest ? { kind: 'test' } : {}) as never });
            }}
          >
            삭제
          </Button>
        }
      />
      {del.error ? <ErrorBox error={del.error} /> : null}
    </Card>
  );
}

/**
 * 브랜드 추가 — 이름만 받아 만들고 **상세 페이지로 넘어간다**. 나머지(브랜드명·링크·프로필)는 상세에서 채운다
 * (운영자 결정 2026-10-11 — 편집 창과 상세를 나누지 않는다). 소속 계정은 「계정」 화면에서 붙인다
 */
function CreateBrandDialog({ open, isTest, onClose }: { open: boolean; isTest: boolean; onClose: () => void }) {
  const create = useApiMutation(registry.createBrand);
  const navigate = useNavigate();
  const [name, setName] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await create.mutateAsync({ body: { name: name.trim(), isTest } }).catch(() => null);
    if (!r) return;
    setName('');
    onClose();
    await navigate({ to: PATHS.admin.brand, params: { id: r.brand.id } });
  };

  return (
    <Dialog open={open} onClose={onClose} title={isTest ? '테스트 브랜드 추가' : '브랜드 추가'}>
      <form className="grid gap-3" onSubmit={(e) => void submit(e)}>
        <Label>
          이름
          <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </Label>
        <p className="text-slate-500 text-xs">만들고 나면 상세 페이지에서 브랜드명·링크·프로필을 채웁니다.</p>
        {isTest ? (
          <p className="text-slate-500 text-xs">
            테스트 브랜드는 「테스트」 탭에만 보입니다. 질문은 실제 고객 질문을 먼저 수집한 뒤 남는 여유로 수집합니다.
          </p>
        ) : null}
        <FieldError>{create.error?.message}</FieldError>
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          만들기
        </Button>
      </form>
    </Dialog>
  );
}

export function BrandDetailPage() {
  // 두 영역(/app·/admin)에 같은 컴포넌트가 있다 — from 을 고정하면 다른 쪽에서 매치를 못 찾아 던진다
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe();
  const brand = useApi(registry.getBrand, { params: { id } });
  const isAdmin = me.data?.role === 'admin';
  // 「ChatGPT | Gemini」 탭 — 운영자만(261104-gemini-concurrent-lane). 이 화면은 /app 에도 걸려 있다
  const tabEngine = useEngine();
  const engine = isAdmin ? tabEngine : undefined;
  const overview = useApi(metrics.overview, { query: { brandId: id, engine } }, { enabled: me.data !== undefined });
  if (brand.isLoading) return <Loading />;
  if (brand.error) return <ErrorBox error={brand.error} />;
  const c = brand.data!.brand;
  return (
    <>
      {/* 운영자는 아래 「브랜드 정보」가 브랜드명·상태를 다 보인다 — 머리는 이름만 */}
      <PageTitle
        title={
          <>
            {c.name}
            {isAdmin && c.isTest ? <TestBadge /> : null}
          </>
        }
      />
      {/* ★ 운영자만 — 정보 보기와 편집이 한 자리다. 안의 라우트가 전부 `admin` 이고 이 화면은 /app 에도 걸려 있다 */}
      {isAdmin ? <BrandInfoAdmin brand={c} /> : null}
      {isAdmin ? <BrandAccountsAdmin brandId={id} /> : null}
      {isAdmin ? <BrandArticlePhotosAdmin brand={c} /> : null}
      {isAdmin ? <BrandBillingAdmin brandId={id} brandStatus={c.status} /> : null}
      {isAdmin ? <PartnerCard brandId={id} admin /> : null}
      <Card>
        <CardHeader
          title={engine ? `질문별 지표 — ${engineLabel(engine)}` : '질문별 지표'}
          description="최근 7일"
          actions={
            isAdmin ? (
              <div className="flex items-center gap-3">
                <EngineTabs />
                <AddQueryAdmin brand={c} />
              </div>
            ) : undefined
          }
        />
        <CardBody className="p-0">
          {overview.isLoading ? (
            <Loading />
          ) : (
            <DataTable
              columns={queryColumns(isAdmin, false, !isAdmin)}
              data={overview.data?.rows ?? []}
              empty="등록된 질문이 없습니다"
              // 질문 상세는 운영자 영역에만 있다 — 고객·대행사에게는 클릭이 없다
              rowLink={
                isAdmin
                  ? (r) => ({
                      to: PATHS.admin.query,
                      params: { id: r.queryId },
                      search: engine ? engineSearch(engine) : {},
                    })
                  : undefined
              }
            />
          )}
        </CardBody>
      </Card>
      {/* ★ 운영자만 — 안의 라우트가 전부 `admin` 이고, 이 화면은 /app 에도 걸려 있다 (261007-admin-brand-works) */}
      {isAdmin ? <BrandWorksAdmin brandId={id} /> : null}
      {isAdmin ? <BrandReportCardAdmin brandId={id} /> : null}
      {isAdmin ? <BrandTestAdmin brandId={id} isTest={c.isTest} /> : null}
      {isAdmin ? <BrandDeleteAdmin brandId={id} name={c.name} isTest={c.isTest} /> : null}
    </>
  );
}
